<template>
  <v-app>
    <AppNavigation v-model:drawer="drawer" />
    
    <v-app-bar color="primary" dark>
      <v-app-bar-nav-icon
        v-if="authStore.isAuthenticated"
        @click="toggleDrawer"
        :aria-label="$t('common.toggleMenu')"
        :title="$t('common.toggleMenu')"
      />
      
      <v-app-bar-title>{{ authStore.appName }}</v-app-bar-title>
      
      <v-spacer></v-spacer>
      
      <v-btn icon @click="toggleTheme" :aria-label="$t('common.toggleTheme')" :title="$t('common.toggleTheme')">
        <v-icon>{{ themeIcon }}</v-icon>
      </v-btn>
      
      <v-btn icon @click="toggleLanguage" :aria-label="$t('common.toggleLanguage')" :title="$t('common.toggleLanguage')">
        <v-icon>mdi-translate</v-icon>
      </v-btn>
    </v-app-bar>

    <v-main>
      <v-container class="py-0">
        <AppBreadcrumb />
      </v-container>
      <router-view />
    </v-main>

    <AppSnackbar />
  </v-app>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, ref } from 'vue'
import { useTheme } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import AppSnackbar from '@/components/AppSnackbar.vue'
import AppBreadcrumb from '@/components/AppBreadcrumb.vue'
import AppNavigation from '@/components/AppNavigation.vue'
import { getPageTitle } from '@/utils/breadcrumb'

const theme = useTheme()
const { locale, t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

// Navigation drawer state
const drawer = ref(false)

// Update document title to include the current page title
const updateDocumentTitle = () => {
  const pageTitle = getPageTitle(route, t)
  if ((route.name as string) === 'home') {
    document.title = authStore.appName
  } else {
    document.title = `${pageTitle} - ${authStore.appName}`
  }
}

// Watch for relevant changes and update title
watch(() => authStore.appName, updateDocumentTitle, { immediate: true })
watch(() => route.name, updateDocumentTitle)
watch(() => locale.value, updateDocumentTitle)

// Ensure auth config is loaded on mount
onMounted(async () => {
  await authStore.checkAuthConfig()
  updateDocumentTitle()
})

const themeIcon = computed(() => 
  theme.global.name.value === 'dark' ? 'mdi-brightness-7' : 'mdi-brightness-4'
)

const toggleTheme = () => {
  theme.global.name.value = theme.global.name.value === 'dark' ? 'light' : 'dark'
}

const toggleLanguage = () => {
  locale.value = locale.value === 'en' ? 'de' : 'en'
}

const toggleDrawer = () => {
  drawer.value = !drawer.value
}
</script>